<template>
  <NuxtLink
    ref="cardEl"
    v-element-visibility="scrolledIntoView"
    :to="urlToListing"
    :title="computedTitle ?? undefined"
    :target="linkTarget"
    :class="
      [
        // !important to override the default link styles, can be removed when base styles are removed
        'relative overflow-hidden block space-y-3 text-primary-950! no-underline!',
        {
          'animate-pulse cursor-progress': isStillLoading,
          'cursor-pointer': !isStillLoading,
          'bg-white rounded-lg shadow-md': variant === 'map',
        },
      ]
    "
   
    @click="rvClicked"
  >
    <div
      :class="['relative block aspect-3/2 overflow-hidden', {
        'rounded-lg': variant !== 'map',
        'rounded-t-lg': variant === 'map',
      }]"
    >
      <AtomTransition timing="fast">
        <TSkeleton
          v-if="isStillLoading"
          key="loader"
          fill
        />
        <TSwipeImages
          v-else
          key="content"
          :contents="photos"
          @content-viewed="updateImageIndex"
        />
      </AtomTransition>

      <AtomTransition timing="instant">
        <div v-if="!isLoading && rv.Badge">
          <LazyCardRvBadge
            :badge="rv.Badge"
            class="absolute top-2 left-2 truncate max-w-2/3 shadow-md"
          />
        </div>
      </AtomTransition>

      <AtomTransition timing="instant">
        <div
          v-if="!isStillLoading"
          class="absolute top-2 right-2 z-2 flex gap-2"
        >
          <LazyFavouritesButton
            appearance="card"
            :rv-id="rv.Id!"
          />

          <button
            v-if="hasCloseButton"
            class="size-8 rounded-full bg-white/75 flex items-center justify-center cursor-pointer"
            type="button"
           
            @click.stop.prevent="emit('close')"
          >
            <TIcon :icon="['far', 'xmark']" />
          </button>
        </div>
      </AtomTransition>
    </div>

    <div :class="{ 'px-3 pb-3': variant === 'map' }">
      <div :class="['relative h-6', { 'mb-1': !isSeoVariant }]">
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-6 w-full"
          />
          <div
            v-else
            v-element-visibility="visibilityChanged"
            class="flex items-start"
          >
            <div class="w-full truncate h-6 font-semibold">
              {{ title }}
            </div>

            <LazyTRating
              v-if="!isSeoVariant && rv.AverageRating"
              :rating="rv.AverageRating"
              :number-of-reviews="rv.NumberOfReview"
              is-compact
            />
          </div>
        </AtomTransition>
      </div>

      <div :class="['relative mb-3 text-sm text-gray-500', { 'h-10': !isSeoVariant, 'h-6': isSeoVariant }]">
        <AtomTransition
          v-if="!isSeoVariant"
          timing="fast"
        >
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-5 w-2/3"
          />
          <div
            v-else
            key="content"
          >
            <div
              class="truncate"
             
            >
              {{ name }}
            </div>
          </div>
        </AtomTransition>

        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            :class="['h-5 w-4/5', { 'top-5 mt-px': !isSeoVariant }]"
          />
          <div
            v-else
            key="content"
            class="flex items-center"
          >
            <template v-if="isSeoVariant">
              <span v-if="props.rv.Year">{{ props.rv.Year }} &bull;&nbsp;</span>
              <span
                v-if="rv.NumberOfReview"
                class="inline-flex items-center gap-1"
              >
                <TRating
                  :rating="rv.AverageRating"
                  :number-of-reviews="rv.NumberOfReview"
                  is-compact
                />
              </span>
              <template v-else>{{ t('newListing') }}</template>

            </template>
            <template v-else>
              <div class="flex items-center gap-1">
                <div class="min-w-0 truncate">
                  {{ meta }}
                </div>
                <div
                  v-if="showDistanceAway"
                  class="flex-none"
                >
                  · {{ distance }}
                </div>
              </div>
            </template>
          </div>
        </AtomTransition>
      </div>

      <div class="relative h-8">
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            fill
            class="w-2/3"
          />
          <div
            v-else
            key="content"
            class="flex items-center flex-wrap"
          >
            <TIcon
              v-if="rv.InstabookOwnerOptedIn"
              :icon="['fas', 'bolt']"
              class="text-secondary mr-1"
            />

            <span
              v-if="rv.DiscountPercent ?? 0 > 0"
             
              class="md:text-sm/6 line-through whitespace-nowrap text-gray-500 mr-2"
            >{{
              priceDisplay({
                value: rv.PreDiscountedAverageNightlyPrice,
                countryCode: rv.Country as COUNTRY_SHORTS,
                showCurrencyCode: false,
                round: true,
              })
            }}</span>

            <span
             
              class="font-semibold text-lg/6 md:text-base flex items-center whitespace-nowrap mr-2"
            >
              {{
                priceDisplay({
                  value: Math.max(rv.DiscountedAverageNightlyPrice ?? 0, 0) || rv.DefaultPrice,
                  countryCode: rv.Country as COUNTRY_SHORTS,
                  showCurrencyCode: true,
                  round: true,
                })
              }}
              <span class="text-sm/6 font-normal">/{{ t('night') }}</span>
            </span>

            <TBadge
              v-if="isDisplayDiscountExperimentEnabled && (rv.DiscountPercent ?? 0 > 0)"
              class="ml-auto"
              size="sm"
              variant="highlight"
            >
              {{ t('rv.discount', { discount: formatWithPrecision(((rv.DiscountPercent ?? 0) * 100), 0) }) }}
            </TBadge>
          </div>
        </AtomTransition>
      </div>

      <div
        v-if="rv.Tags?.length"
        class="flex gap-2 relative h-6 mt-2"
      >
        <AtomTransitionGroup timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            fill
            class="w-1/3"
          />
          <template v-else>
            <LazyCardRvTag
              v-for="tag in rv.Tags"
              :key="tag.Title.En"
              :tag="tag"
            />
          </template>
        </AtomTransitionGroup>
      </div>

      <!-- Admin-only diagnostics. The `v-if` is what makes the lazy import pay off: without a guard
           Nuxt still creates the async component and fetches its chunk for every card, so the strip
           only mounts (and its chunk loads) when the admin-only Metadata is actually present. -->
      <LazyCardRvAdminMetadata
        v-if="rv.Metadata?.length"
        :metadata="rv.Metadata"
      />
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { RV_MANUFACTURERS_NOT_APPLICABLE, RV_MANUFACTURERS_OTHER } from '~/constants/manufacturers'

import type { Coordinate, RvAdminMetadata, RvExpandedDetails, RvUnifiedSearch } from '~/types'
import type { COUNTRY_SHORTS } from '~/constants'
import type { IMAGE_PRESETS } from '~/constants/image'
import type { DiscoverySource, RvCardCtaType } from './types'
import type { SwipeImage } from '~/components/_tailwind/swipe/types'

const props = withDefaults(defineProps<{
  index?: number
  rv: RvExpandedDetails & { Badge?: RvUnifiedSearch['Badge'], Tags?: RvUnifiedSearch['Tags'], Metadata?: RvAdminMetadata }
  isLoading?: boolean
  cta?: RvCardCtaType
  discoverySource?: DiscoverySource
  searchRequestId: string
  visibilityThreshold?: number
  visibilityThrottle?: number
  destinationName?: string
  variant?: 'gallery' | 'map'
  trackVisiblity?: boolean
  showFeatured?: boolean
  hasCloseButton?: boolean
  imagePreset?: Extract<keyof typeof IMAGE_PRESETS, `LANDSCAPE_${string}`>
  /**
   * Determines wether or not a card should load when it scrolls into view
   * (default), or imediately (useful for SEO).
   */
  noLazyLoad?: boolean
  eagerLoad?: boolean
  pageSize?: number
  listPageNumber?: number
  isSeoVariant?: boolean
}>(), {
  index: 0,
  visibilityThreshold: 0.5,
  visibilityThrottle: 500,
  variant: 'gallery',
  pageSize: 10,
  listPageNumber: 1,
  imagePreset: 'LANDSCAPE_MD',
})

const emit = defineEmits<{
  'visible:rv': [{
    rv: RvExpandedDetails
    cta?: RvCardCtaType
    requestId: string
  }]
  'scrolled-into-view': [index: number]
  'close': []
}>()

const { t, locale } = useI18n()
const { priceDisplay } = usePrice()
const { checkIfRvIsFavourited } = useFavouritesData()
const { routeBaseName } = useBaseName()
const route = useRoute()
const { isWebView } = usePlatform()
const { isMobile } = useDevice()
const { $captureError } = useNuxtApp()
const { isPotentiallyABot } = useBotScore()
const { getDynamicImage, presets } = useImage()
const { isFeatureEnabled } = useExperiment()
const searchData = useSearchData()

const imageIndex = ref(0)

/**
 * Tracks wether or not the card is scrolled into view.
 */
const isVisible = ref(props.noLazyLoad)
const hasBeenScrolledIntoView = ref(false)
const hasBeenTracked = ref(false)

const isStillLoading = computed(() => {
  return props.isLoading || !isVisible.value
})

const showDistance = computed(() => {
  const searchLocationParams = getNormalizedSearchLocationParams(searchData.value.location)
  return Boolean(
    (
      props.destinationName
      && props.destinationName !== props.rv.City
    )
    || searchLocationParams?.city,
  )
})

function getCoordinateValue(coordinate: (() => number) | number) {
  if (typeof coordinate === 'function') {
    return coordinate()
  }
  else {
    return coordinate
  }
}

const getDistanceAway = computed(() => {
  const lat = searchData.value.location?.coordinates?.lat
  const lng = searchData.value.location?.coordinates?.lng

  if (!lat || !lng || !props.rv.Latitude || !props.rv.Longitude) {
    return null
  }

  const coordinate1: Coordinate = {
    lat: getCoordinateValue(lat),
    lng: getCoordinateValue(lng),
  }
  const coordinate2: Coordinate = {
    lat: props.rv.Latitude,
    lng: props.rv.Longitude,
  }

  return distanceBetweenTwoPoints(coordinate1, coordinate2)
})

const rvMeasurementUnit = computed(() => {
  if (!props.rv.Country) return

  return countryCodeToDistanceUnit(props.rv.Country)
})

const rvLocalizedRVType = computed(() => props.rv.RVType ? `rvType.${props.rv.RVType}` : 'rvType.rvCottage')

const distanceAway = computed(() => {
  if (!getDistanceAway.value || !rvMeasurementUnit.value) return

  return formatDistance(getDistanceAway.value, rvMeasurementUnit.value, locale.value)
})

const showDistanceAway = computed(() => {
  return props.cta !== 'map' && showDistance.value && distanceAway.value
})

const computedTitle = computed(() => {
  if (props.isSeoVariant) {
    return [props.rv.Make !== RV_MANUFACTURERS_OTHER && props.rv.Make !== RV_MANUFACTURERS_NOT_APPLICABLE ? props.rv.Make : undefined, t(rvLocalizedRVType.value)].filter((key) => Boolean(key)).join(' ')
  }

  return props.rv.RVName
})

const localePath = useLocalePath()
const urlToListing = computed(() => {
  return localePath(generateRvPath(props.rv?.AliasName ?? '', isPotentiallyABot
    ? undefined
    : {
        PlaceId: route.query?.PlaceId,
        StartDate: route.query?.StartDate,
        EndDate: route.query?.EndDate,
        Adults: route.query?.Adults,
        Children: route.query?.Children,
        PetFriendly: route.query?.PetFriendly,
        Delivery: route.query?.Delivery,
      }))
})

const imagePreset = computed(() => {
  return presets[props.imagePreset]
})

const photos = computed(() => {
  if (!props.rv.Photos) {
    return []
  }

  const images: SwipeImage[] = props.rv.Photos.filter((photo) => photo.Path).map((photo, index) => ({
    type: 'image' as const,
    path: photo.Path ?? '',
    src: getDynamicImage({
      path: photo.Path,
      preset: imagePreset.value,
    }) ?? '',
    width: imagePreset.value.width,
    height: imagePreset.value.height,
    description: props.rv.RVName ?? '',
    loading: props.eagerLoad && index === 0 ? 'eager' : 'lazy',
  }))

  return images
})

const openSameWindow = computed(() => {
  return Boolean(route.meta?.layout === 'minimal' || isWebView.value || isMobile)
})

const linkTarget = computed(() => {
  if (openSameWindow.value) {
    return
  }
  return '_blank'
})

/**
 * Triggers when the card has scrolled into (or out of) view.
 *
 * @param visible Whether the card is visible
 * @returns A promise that resolves when the card has scrolled into (or out of) view
 */
function scrolledIntoView(visible: boolean) {
  /**
   * If we don't want to lazy load content, or when the card has scrolled
   * out of view, or when it has already been triggered, exit early.
   *
   * Otherwise fire the 'scrolled-into-view' event and track it locally.
   */
  if (props.noLazyLoad || !visible || hasBeenScrolledIntoView.value) return

  emit('scrolled-into-view', props.index)

  isVisible.value = visible
  hasBeenScrolledIntoView.value = true
}

function updateImageIndex(index: number) {
  imageIndex.value = index
}

const cardEl = useTemplateRef('cardEl')
function trackClick() {
  const parent = cardEl.value?.$el.parentElement

  if (!cardEl.value || !parent) {
    return $captureError(new Error('trackClick called, but template ref or parent element do not exist'), {
      cardRef: cardEl.value,
    })
  }

  const listIndex = [...parent.children].findIndex((el) => el === cardEl.value)
  const columnCount = window.getComputedStyle(parent).getPropertyValue('grid-template-columns').split(' ').length
  const listRowNumber = Math.floor(listIndex / columnCount) + 1
  const listColumnNumber = (listIndex % columnCount) + 1

  const rv = getRvTrackingProperties()

  if (!rv) {
    return
  }

  trackListingClicked({
    cta: props.cta || routeBaseName.value,
    discoverySource: props.discoverySource ?? 'Unknown',
    distanceShown: showDistance.value,
    hasDiscount: (props.rv?.DiscountPercent ?? 0) > 0,
    hasFlexibleDates: undefined,
    isMapOpen: document.getElementsByClassName('map-wrapper').length > 0,
    isShortStay: props.rv.IsShortStay ?? false,
    isSuperhost: props.rv.IsSuperHostActive ?? false,
    listCardVariant: props.variant,
    listColumnNumber,
    listRowNumber,
    pageSource: routeBaseName.value,
    photoPath: photos.value[imageIndex.value]?.path ?? '',
    photoPosition: imageIndex.value + 1,
    rv,
    rvType: rv.listingType,
  })
}

function rvClicked() {
  if (isStillLoading.value) {
    return
  }

  trackClick()
  setRvDetailsPageReferrerAndCta()
}

const absoluteLocalePath = useAbsoluteLocalePath()

function visibilityChanged(visible: boolean) {
  if (!visible || hasBeenTracked.value) return

  hasBeenTracked.value = true

  if (!props.trackVisiblity) {
    emit('visible:rv', {
      rv: props.rv,
      cta: props.cta,
      requestId: props.searchRequestId,
    })
    return
  }

  const listing = getRvTrackingProperties()

  if (!listing) {
    return
  }

  trackListingSetViewed({
    params: {
      cta: props.cta,
      listingPageNumber: props.listPageNumber,
      listingPageSize: props.pageSize,
      listings: [listing],
      pageSource: routeBaseName.value,
    },
    additionalParameters: {
      rvUrlListings: [{
        rvUrl: absoluteLocalePath(generateRvPath(props.rv?.AliasName ?? '')),
      }],
    },
  })
}

function getRvTrackingProperties(): TrackedListing | undefined {
  const details = getRvDetails(props.rv)
  const { rvDefaultPrice, rvDiscountedAverageNightlyPrice, rvId, rvPhotos } = details

  return toTrackedListing(details, {
    distanceUnit: rvMeasurementUnit.value === 'miles' ? 'mi' : 'km',
    heroImage: getDynamicImage({
      path: rvPhotos?.[0]?.Path,
      preset: imagePreset.value,
    }) ?? '',
    isFavourite: Boolean(checkIfRvIsFavourited(rvId)),
    nightlyRate: Math.max(rvDiscountedAverageNightlyPrice ?? 0, 0) || rvDefaultPrice,
  })
}

function setRvDetailsPageReferrerAndCta() {
  if (props.cta) {
    // Add rvId to the key, so it's unique to each RV.
    // Localstorage was used instead of sessionStorage, as sessionStorage didn't work when opening certain links in a new tab
    window.localStorage.setItem(`rvDetailsPageCta_${props.rv.Id}`, props.cta)
  }

  // Store the current route in localStorage. The RV Details page is opened in a new tab, so context does not contain the route
  window.localStorage.setItem(`rvDetailsPageCtaReferrer_${props.rv.Id}`, routeBaseName.value || '')
}

/**
 * rvz22746DisplayDiscountInPricing
 *
 * This experiment shows a discount badge in the pricing breakdown.
 */
const isDisplayDiscountExperimentEnabled = computed(() => isFeatureEnabled('rvz22746DisplayDiscountInPricing'))

/**
 * rvz26877AdjustedRvCardContents
 *
 * This experiment uses alternate titles and meta data on the card.
 */
const title = computed(() => `${t(rvLocalizedRVType.value)} ${t('in')} ${props.rv.City}`)
const name = computed(() => `${props.rv.RVName}`)
const distance = computed(() => `${distanceAway.value}`)
const meta = computed(() => `${props.rv.Year} · ${t('sleeps')} ${props.rv.Guests}`)
</script>

<i18n lang="json">
{
  "en": {
    "away": "{distance} away",
    "night": "night",
    "sleeps": "Sleeps",
    "newListing": "New listing",
    "in": "in"
  },
  "fr": {
    "away": "à {distance}",
    "night": "nuit",
    "sleeps": "Couche",
    "newListing": "Nouvelle annonce",
    "in": "·"
  }
}
</i18n>
